/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { useEffect, useState } from 'react';
import { Asset, AssetUtils } from '@wit/vtv-x-foundation-lib';
import translate from '../services/i18n/translate';
import useIsAdultContent from './useIsAdultContent';

export default function useAssetTitle(asset: Asset | undefined, includeEpisodeInfo = true) {
  const [assetTitle, setAssetTitle] = useState('');
  const isAdultContent = useIsAdultContent(asset);

  useEffect(() => {
    if (!asset || isAdultContent === undefined) {
      setAssetTitle('');
      return;
    }

    if (isAdultContent) {
      setAssetTitle(translate['programInfoBar.adultContent.description']);
      return;
    }

    const title = asset.title || translate['watchtv.programInformationUnavailable'];

    if (AssetUtils.isTvAsset(asset) || AssetUtils.isEpisode(asset)) {
      const { seasonNumber, episodeNumber } = asset;
      const isSpecialSeason = AssetUtils.isSpecialSeasonNumber(asset.seasonNumber);

      if (isSpecialSeason || !seasonNumber || !includeEpisodeInfo) {
        setAssetTitle(asset.seriesName || title);
        return;
      }

      const seasonNumberLabel = translate.formatString(translate['asset.seasonFormat'], seasonNumber).toString();

      if (!episodeNumber) {
        setAssetTitle(`${asset.seriesName || title} - ${seasonNumberLabel}`);
        return;
      }

      const episodeNumberLabel = translate.formatString(translate['asset.episodeFormat'], episodeNumber).toString();

      setAssetTitle(`${asset.seriesName || title} - ${seasonNumberLabel}, ${episodeNumberLabel}`);
      return;
    }

    setAssetTitle(title);
  }, [isAdultContent, asset, includeEpisodeInfo]);

  return assetTitle;
}
